/**
 * Variant Preservation Patch v6.2
 * - Fix: option label pollution (e.g., "Metal Type 18K Yellow Gold" becoming the key)
 * - Cleans legend/label by removing selected-value nodes before reading text
 * - Robust swatch (button/ARIA) + radios + selects support
 * - Encoded variant param keys; restore + URL clean
 * 
 * 
 * Applicable for Metal Type, Band Shape, Prongs
 * Not applicable for Second Stone, Dimension, Eternity Arc
 */
(function () {

  let isRedirecting = false;
  const attachedListeners = new WeakMap();

  // ---------- Utils ----------
  const norm = (s) => (s || '')
    .replace(/\u00A0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  const afterUiSettled = (cb) => {
    requestAnimationFrame(() => setTimeout(cb, 0));
  };

  function textOfFirstTextNode(el) {
    if (!el) return '';
    for (const n of el.childNodes) {
      if (n.nodeType === Node.TEXT_NODE) {
        return String(n.nodeValue || '');
      }
    }
    return '';
  }

  function cleanLabelFromNode(node) {
    if (!node) return '';
    const clone = node.cloneNode(true);

    // Remove nodes that typically hold the *current selected value*
    clone.querySelectorAll([
      '[id^="selected"]',
      '.selected-value',
      '.product-variant__selected',
      '.value',
      '.pill__value',
      '.swatch__value',
      '.variant__value'
    ].join(',')).forEach(n => n.remove());

    // Prefer the first text node if present, otherwise full textContent
    let label = textOfFirstTextNode(clone).trim();
    if (!label) label = (clone.textContent || '').trim();

    // Strip trailing ":" and collapse spaces
    label = label.replace(':', '').replace(/\s+/g, ' ').trim();

    // Heuristic: if the label accidentally still contains a newline or long tail,
    // take content before the first newline or 3+ spaces sequence.
    label = label.split('\n')[0].trim().replace(/\s{3,}.+$/, '').trim();

    // Safety: if it starts with "metal type", standardize to exactly that
    if (norm(label).startsWith('metal type')) label = 'Metal Type';

    return label;
  }

  function getFieldsetOptionName(fieldset) {
    if (!fieldset) return '';

    // 1) Attribute hints first
    const viaData  = fieldset.getAttribute('data-option-name');
    if (viaData) return cleanLabelFromNode({ textContent: viaData });

    // 2) aria-labelledby → referenced label/legend
    const labelledBy = fieldset.getAttribute('aria-labelledby');
    if (labelledBy) {
      const labelEl = document.getElementById(labelledBy);
      const label = cleanLabelFromNode(labelEl);
      if (label) return label;
    }

    // 3) legend or nearby .form__label
    const legend = fieldset.querySelector('legend');
    if (legend) {
      const label = cleanLabelFromNode(legend);
      if (label) return label;
    }

    const formLabel = fieldset.closest('.product-form__input')?.querySelector('.form__label, label');
    if (formLabel) {
      const label = cleanLabelFromNode(formLabel);
      if (label) return label;
    }

    // 4) Fallback: infer from input name "options[0]" etc. (last resort)
    const anyInput = fieldset.querySelector('input[name^="options["], select[name^="options["]');
    if (anyInput && anyInput.name) {
      // If your site standardizes names, you could map index→label here.
      // For safety, return a generic placeholder to keep logic intact.
      return anyInput.name.replace(/^.*\[(\d+)\].*$/, 'Option $1');
    }

    return '';
  }

  function extractUrlFromItem(item, originalOnclick) {
    const a = item.closest('a') || item.querySelector('a');
    if (a && a.href) return new URL(a.href, window.location.origin);
    if (item.dataset) {
      const dataHref = item.dataset.href || item.dataset.url;
      if (dataHref) return new URL(dataHref, window.location.origin);
    }
    const s = originalOnclick ? String(originalOnclick) : '';
    const m =
      s.match(/window\.location(?:\.href)?\s*=\s*['"]([^'"]+)['"]/) ||
      s.match(/location\.assign\(\s*['"]([^'"]+)['"]\s*\)/) ||
      s.match(/location\.replace\(\s*['"]([^'"]+)['"]\s*\)/);
    if (m && m[1]) return new URL(m[1], window.location.origin);
    return null;
  }

  // ---------- Swatch helpers (for Metal Type etc.) ----------
  function getCandidateSwatchValue(el) {
    let v = el?.dataset?.value
      || el?.dataset?.swatchValue
      || el?.getAttribute?.('data-option-value')
      || el?.getAttribute?.('aria-label')
      || el?.getAttribute?.('title')
      || '';

    if (!v) v = (el?.textContent || '').trim();

    if (!v) {
      const valueEl = el?.querySelector?.(
        '.swatch__value, .swatch__label, .pill__label, .value, [data-value]'
      );
      if (valueEl) {
        v = valueEl.dataset?.value || valueEl.textContent?.trim() || '';
      }
    }

    if (!v) {
      const img = el?.querySelector?.('img[alt]');
      if (img?.alt) v = img.alt.trim();
    }
    return v;
  }

  function findSelectedSwatchInFieldset(fieldset) {
    const selectors = [
      '[aria-checked="true"]',
      '[aria-selected="true"]',
      '[aria-pressed="true"]',
      '.is-selected',
      '.is-active',
      '.selected',
      '.swatch__option--selected',
      '.swatch__button--selected'
    ];
    for (const sel of selectors) {
      const el = fieldset.querySelector(sel);
      if (el) return el;
    }
    const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
    if (checkedRadio) {
      const maybeButton = checkedRadio.closest('button, .swatch__option, .pill');
      if (maybeButton) return maybeButton;
    }
    return null;
  }

  function clickMatchingSwatch(fieldset, desiredValue) {
    const desired = norm(desiredValue);
    const clickables = fieldset.querySelectorAll(
      'button, [role="radio"], [role="option"], .swatch__option, .pill, [data-value], input[type="radio"] + label, label'
    );

    for (const el of clickables) {
      const readFrom = el.matches('label') && el.getAttribute('for')
        ? document.getElementById(el.getAttribute('for'))?.closest('button, .swatch__option, .pill') || el
        : el;

      const val = getCandidateSwatchValue(readFrom);
      if (val && norm(val) === desired) {
        if (el.matches('button,[role="radio"],[role="option"],.swatch__option,.pill, label, input[type="radio"]')) {
          el.click();
        } else {
          const input = fieldset.querySelector('input[type="radio"]');
          if (input) { input.checked = true; input.dispatchEvent(new Event('change', { bubbles: true })); }
        }
        return true;
      }
    }
    return false;
  }

  // ---------- Read current selections ----------
  function getCurrentVariantSelections() {
    const selections = {};

    // Method 1: swatch/pill/radio fieldsets (cleaned option names)
    document.querySelectorAll('fieldset.js.product-form__input.product-form__input--pill, fieldset[data-option-name], .product-form__input fieldset')
      .forEach((fieldset) => {
        let optionName = getFieldsetOptionName(fieldset);
        if (!optionName) return;

        const selectedSwatch = findSelectedSwatchInFieldset(fieldset);
        if (selectedSwatch) {
          const v = getCandidateSwatchValue(selectedSwatch);
          if (v) { selections[optionName] = v.trim(); return; }
        }

        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (checked) {
          let labelText = '';
          const labels = fieldset.querySelectorAll('label');
          for (const lab of labels) {
            if (lab.getAttribute('for') === checked.id) { labelText = lab.textContent || ''; break; }
          }
          if (labelText) { selections[optionName] = labelText.trim(); return; }
        }

        const selectedSpan = fieldset.querySelector('span[id^="selected"], .selected-value');
        if (selectedSpan) {
          const v = (selectedSpan.textContent || '').trim();
          if (v) { selections[optionName] = v; return; }
        }
      });

    // Method 2: product group pickers (radios)
    document.querySelectorAll('input[type="radio"][name^="related_product_"]:checked')
      .forEach((radio) => {
        const fieldset = radio.closest('fieldset');
        if (!fieldset) return;
        const optionName = getFieldsetOptionName(fieldset);
        if (!optionName) return;

        let valueText = '';
        const labels = fieldset.querySelectorAll('label');
        for (const label of labels) {
          if (label.getAttribute('for') === radio.id) { valueText = (label.textContent || '').trim(); break; }
        }
        if (valueText && !/sold_out/i.test(valueText)) {
          selections[optionName] = valueText;
        }
      });

    // Method 3: regular fieldsets (non-pill)
    document.querySelectorAll('.product-form__input:not(.product-form__input--pill) fieldset')
      .forEach((fieldset) => {
        const optionName = getFieldsetOptionName(fieldset);
        if (!optionName || selections[optionName]) return;

        const checkedInput = fieldset.querySelector('input:checked');
        if (!checkedInput) return;

        const label = fieldset.querySelector(`label[for="${checkedInput.id}"]`);
        const valueText = (label?.textContent || '').trim();
        if (valueText) selections[optionName] = valueText;
      });

    // Method 4: dropdown selects (main product)
    document.querySelectorAll('.product-form__input--dropdown select, .product-form__input select')
      .forEach((select) => {
        const container = select.closest('.product-form__input');
        if (!container) return;
        const label = container.querySelector('.form__label, label');
        if (!label || !select.value) return;

        let optionName = cleanLabelFromNode(label);
        if (!optionName) return;

        const selectedOption = select.options[select.selectedIndex];
        if (selectedOption) {
          selections[optionName] = (selectedOption.textContent || '').trim();
        }
      });

    return selections;
  }

  // ---------- Redirect with params ----------
  function performRedirect(url, selections) {
    if (isRedirecting) {
      return;
    }
    isRedirecting = true;

    Object.entries(selections).forEach(([key, value]) => {
      // sanitize key once more before encoding
      let cleanKey = String(key).replace(/\s+/g, ' ').trim();
      if (norm(cleanKey).startsWith('metal type')) cleanKey = 'Metal Type';
      const paramKey = 'variant_' + encodeURIComponent(cleanKey);
      url.searchParams.set(paramKey, value);
    });

    window.location.href = url.toString();
  }

  // ---------- Interceptors for product group pickers ----------
  function interceptShapeContainer() {
    const items = document.querySelectorAll('.shape-container .list-menu__item');
    if (!items.length) return;

    items.forEach((item) => {
      if (attachedListeners.has(item)) {
        item.onclick = attachedListeners.get(item);
      }
      const originalOnclick = item.onclick;
      attachedListeners.set(item, originalOnclick);

      item.onclick = function (event) {
        if (isRedirecting) return false;
        event.preventDefault();

        const url = extractUrlFromItem(item, originalOnclick);

        afterUiSettled(() => {
          const selections = getCurrentVariantSelections();
          performRedirect(url, selections);
        });
        return false;
      };
    });
  }

  function interceptOtherPickers() {
    // Related product radios
    const radios = document.querySelectorAll('input[type="radio"][name^="related_product_"]');

    radios.forEach((radio) => {
      if (attachedListeners.has(radio)) {
        radio.removeEventListener('change', attachedListeners.get(radio));
      }
      const handler = function (e) {
        if (isRedirecting) return false;
        if (this.checked && this.value) {
          e.preventDefault();
          e.stopImmediatePropagation();

          const url = new URL(this.value, window.location.origin);
          afterUiSettled(() => {
            const selections = getCurrentVariantSelections();
            performRedirect(url, selections);
          });
          return false;
        }
      };
      attachedListeners.set(radio, handler);
      radio.addEventListener('change', handler);
    });

    // Related product dropdowns
    const dropdowns = document.querySelectorAll('select[id^="ProductSelect-related-"]');

    dropdowns.forEach((dropdown) => {
      if (attachedListeners.has(dropdown)) {
        dropdown.removeEventListener('change', attachedListeners.get(dropdown));
      }
      const handler = function (e) {
        if (isRedirecting) return false;

        e.preventDefault();
        e.stopImmediatePropagation();

        const url = new URL(this.value, window.location.origin);
        afterUiSettled(() => {
          const selections = getCurrentVariantSelections();
          performRedirect(url, selections);
        });
        return false;
      };
      attachedListeners.set(dropdown, handler);
      dropdown.addEventListener('change', handler);
    });
  }

  // ---------- Restore from URL variant_ params ----------
  function restoreVariantSelections() {
    const params = new URLSearchParams(window.location.search);
    const keys = [...params.keys()].filter((k) => k.startsWith('variant_'));
    if (!keys.length) return;

    setTimeout(() => {
      let restored = 0;

      const allFieldsets = Array.from(document.querySelectorAll('fieldset, fieldset[data-option-name]'));

      keys.forEach((rawKey) => {
        // Sanitize noisy keys like "Metal Type   18K Yellow Gold"
        let optionName = decodeURIComponent(rawKey.slice(8)).replace(/\s+/g, ' ').trim();
        // Keep only the "label-looking" head (before newline/long tail)
        optionName = optionName.split('\n')[0].replace(/\s{3,}.+$/, '').trim();
        if (norm(optionName).startsWith('metal type')) optionName = 'Metal Type';

        const desired = (params.get(rawKey) || '').trim();
        if (!optionName || !desired) return;

        const optNorm = norm(optionName);
        const valNorm = norm(desired);

        // 1) Try matching fieldset by cleaned option name
        let matchedFieldset = null;
        for (const fieldset of allFieldsets) {
          const fsName = getFieldsetOptionName(fieldset);
          if (!fsName) continue;
          const fsNorm = norm(fsName);
          if (fsNorm === optNorm || optNorm.includes(fsNorm) || fsNorm.includes(optNorm)) {
            matchedFieldset = fieldset;

            // A) Button/ARIA swatches
            if (clickMatchingSwatch(fieldset, desired)) { restored++; break; }

            // B) Radios
            const radios = fieldset.querySelectorAll('input[type="radio"]');
            let picked = false;
            for (const radio of radios) {
              let labelText = '';
              const localLabels = fieldset.querySelectorAll('label');
              for (const lab of localLabels) {
                if (lab.getAttribute('for') === radio.id) { labelText = lab.textContent || ''; break; }
              }
              if (!labelText) {
                const globalLabel = document.querySelector(`label[for="${radio.id}"]`);
                if (globalLabel) labelText = globalLabel.textContent || '';
              }
              if (norm(labelText) === valNorm) {
                radio.click();
                restored++;
                picked = true;
                break;
              }
            }
            if (picked) break;

            // C) Selects under same container
            const localSelects = fieldset.querySelectorAll('select');
            let selectedFromSelect = false;
            for (const select of localSelects) {
              for (const opt of select.options) {
                if (norm(opt.textContent) === valNorm) {
                  select.value = opt.value;
                  select.dispatchEvent(new Event('change', { bubbles: true }));
                  restored++;
                  selectedFromSelect = true;
                  break;
                }
              }
              if (selectedFromSelect) break;
            }
            if (selectedFromSelect) break;
          }
        }

        // 2) Final fallback: any select labeled with (approx) this option name
        if (!matchedFieldset) {
          const selects = document.querySelectorAll('.product-form__input select, select');
          for (const select of selects) {
            const container = select.closest('.product-form__input');
            const label = container?.querySelector('label, .form__label');
            const labelText = label ? norm(cleanLabelFromNode(label)) : '';
            if (labelText && (labelText === optNorm || optNorm.includes(labelText) || labelText.includes(optNorm))) {
              for (const opt of select.options) {
                if (norm(opt.textContent) === valNorm) {
                  select.value = opt.value;
                  select.dispatchEvent(new Event('change', { bubbles: true }));
                  restored++;
                  break;
                }
              }
            }
          }
        }
      });

      if (restored > 0) {
        const cleanUrl = new URL(window.location.href);
        keys.forEach((k) => cleanUrl.searchParams.delete(k));
        window.history.replaceState({}, '', cleanUrl.toString());
      }
    }, 500);
  }

  // ---------- Init ----------
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }

    setTimeout(() => {
      isRedirecting = false;
      interceptShapeContainer();
      interceptOtherPickers();
      restoreVariantSelections();

      let reInitTimeout = null;
      const observer = new MutationObserver((mutations) => {
        if (reInitTimeout) clearTimeout(reInitTimeout);

        let needs = false;
        for (const m of mutations) {
          if (m.addedNodes?.length) {
            for (const node of m.addedNodes) {
              if (node.nodeType === 1 && node.querySelector) {
                if (
                  node.querySelector('.shape-container') ||
                  node.querySelector('input[name^="related_product_"]') ||
                  node.querySelector('select[id^="ProductSelect-related-"]') ||
                  node.querySelector('[role="radio"], [aria-checked], .swatch__option, .pill')
                ) {
                  needs = true;
                  break;
                }
              }
            }
          }
          if (needs) break;
        }

        if (needs && !isRedirecting) {
          reInitTimeout = setTimeout(() => {
            interceptShapeContainer();
            interceptOtherPickers();
          }, 300);
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });

    }, 200);
  }

  init();
})();